import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import type { Chapter } from "@repo/app-types/entities/course";
import {
  collectPlaybookSections,
  type PlaybookCriterionNode,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { ProjectWithJobType } from "@repo/app-types/entities/project";
import type { TrainingChapterAssessment } from "@repo/app-types/entities/trainingChapterAssessment";
import { parseAverageGradingValue } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Pill } from "@repo/ui/Pills/pill";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useMemo } from "react";
import { Heading } from "react-aria-components";
import { useParams } from "react-router";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useAllTrainingChapterAssessmentsForProjectQuery } from "../../api/entities/trainingChapterAssessment.js";
import {
  AssessmentCriteriaNode,
  AssessmentCriteriaNodeCommentary,
  AssessmentCriteriaNodeGrading,
  AssessmentCriteriaNodeTitle,
} from "../../components/assessment/assessmentCriteriaNode.js";
import {
  AssessmentSectionNode,
  AssessmentSectionNodeContent,
  AssessmentSectionNodeTitle,
} from "../../components/assessment/assessmentSectionNode.js";
import { AssessmentTable } from "../../components/assessment/assessmentTable.js";
import { CriterionAverageScore } from "../teammatePerformance/criterionAverageScore.js";
import { renderAssessableParticipantCount } from "../trainingInsights/trainingInsightsHelpers.js";
import { TrainingAssessmentTemplateScorePill } from "./trainingAssessmentTemplateScorePill.js";

export interface TrainingChapterDialogProps {
  project: ProjectWithJobType;
}

export const TrainingChapterDialog: FC<TrainingChapterDialogProps> = (
  props,
) => {
  const { chapterId } = useParams();
  if (!chapterId) {
    throw new Error("chapterId param missing");
  }

  const { data, isLoading } = useAllTrainingChapterAssessmentsForProjectQuery({
    projectId: props.project.id,
  });

  // Find the assessment for the selected chapter and fetch the playbook that was used to generate it
  const chapterAssessment = data?.trainingChapterAssessments.find(
    (a) => a.chapter_id === chapterId,
  );
  const chapterPlaybookId =
    chapterAssessment?.grading.assessment_template
      ?.recording_assessment_template_id;
  const { data: playbook, isLoading: isLoadingPlaybook } = usePlaybookByIdQuery(
    chapterPlaybookId ?? skipToken,
  );
  const assessmentTree = playbook?.assessment_tree ?? null;

  const renderCriterion = (
    chapter: Chapter,
    assessment: TrainingChapterAssessment | undefined,
    criterion: PlaybookCriterionNode,
  ) => {
    const grade =
      assessment?.grading.assessment_template?.criteria_grading[criterion.id];
    const value = parseAverageGradingValue(
      criterion.grading_value_type,
      grade?.value,
    );

    return {
      grading: (
        <CriterionAverageScore
          valueType={criterion.grading_value_type}
          value={value?.value ?? undefined}
        />
      ),
      commentary: grade?.commentary,
    };
  };

  const sectionNodeIds = useMemo(
    () =>
      assessmentTree
        ? collectPlaybookSections(assessmentTree).map((section) => section.id)
        : [],
    [assessmentTree],
  );

  const renderNode = (
    chapter: Chapter,
    assessment: TrainingChapterAssessment | undefined,
    node: PlaybookNode,
  ) => {
    switch (node.type) {
      case "section": {
        return (
          <AssessmentSectionNode
            key={node.id}
            id={node.id}
            textValue={node.name}
          >
            <AssessmentSectionNodeContent>
              <AssessmentSectionNodeTitle>
                {node.name}
              </AssessmentSectionNodeTitle>
            </AssessmentSectionNodeContent>
            {renderNodes(chapter, assessment, node.nodes)}
          </AssessmentSectionNode>
        );
      }

      case "criteria": {
        const { grading, commentary } = renderCriterion(
          chapter,
          assessment,
          node,
        );

        return (
          <AssessmentCriteriaNode
            key={node.id}
            textValue={node.name}
            onPress={undefined}
          >
            <AssessmentCriteriaNodeTitle>
              {node.name}
            </AssessmentCriteriaNodeTitle>
            <AssessmentCriteriaNodeGrading>
              {grading}
            </AssessmentCriteriaNodeGrading>
            <AssessmentCriteriaNodeCommentary>
              {commentary}
            </AssessmentCriteriaNodeCommentary>
          </AssessmentCriteriaNode>
        );
      }

      default: {
        assertUnreachable(node);
      }
    }
  };

  const renderNodes = (
    chapter: Chapter,
    assessment: TrainingChapterAssessment | undefined,
    nodes: readonly PlaybookNode[],
  ) => {
    return nodes.map((node) => renderNode(chapter, assessment, node));
  };

  const renderContent = (
    chapter: Chapter,
    assessableParticipantsCount: number,
    assessments: readonly TrainingChapterAssessment[],
  ) => {
    const assessment = assessments?.find((a) => a.chapter_id === chapter.id);

    const completedParticipantsCount =
      assessment?.score_details.completed_participants_count;
    const participationRate =
      completedParticipantsCount !== undefined &&
      assessableParticipantsCount !== undefined
        ? assessableParticipantsCount !== 0
          ? completedParticipantsCount / assessableParticipantsCount
          : null
        : undefined;

    const passRate = assessment?.score_details.average_pass_rate;

    return (
      <>
        <section className="space-y-4">
          <Heading className="flex items-baseline gap-2">
            <span className="text-base font-semibold text-foreground">
              Participation rate
            </span>
          </Heading>

          <div className="flex items-center gap-2">
            <Pill
              size={Size.MEDIUM}
              color={Color.Gray}
              label={
                <>
                  {participationRate === undefined ? "--" : null}
                  {participationRate === null ? "n/a" : null}
                  {participationRate !== undefined && participationRate !== null
                    ? (participationRate * 100).toFixed() + "%"
                    : null}
                </>
              }
            />

            <div className="text-xs text-gray-600">
              {renderAssessableParticipantCount(assessableParticipantsCount)}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <Heading className="flex items-baseline gap-2">
              <span className="text-base font-semibold text-foreground">
                Playbook preparedness
              </span>
            </Heading>

            <TrainingAssessmentTemplateScorePill
              size={Size.SMALL}
              score={passRate}
            />
          </div>

          {assessmentTree ? (
            <AssessmentTable
              className="grid-cols-[minmax(0,3fr)_auto_minmax(0,6fr)] gap-x-4"
              defaultExpandedIds={sectionNodeIds}
            >
              {renderNodes(chapter, assessment, assessmentTree.nodes)}
            </AssessmentTable>
          ) : null}
        </section>
      </>
    );
  };

  const chapter = data?.course.chapters.find((c) => c.id === chapterId);
  const chapterLabel =
    data && chapter
      ? `Chapter ${data.course.chapters.indexOf(chapter) + 1}${chapter.topic ? `: ${chapter.topic}` : ""}`
      : null;

  return (
    <SlideoverDialog label={chapterLabel ?? "Loading chapter..."}>
      {({ close }) => (
        <>
          <SlideoverDialogHeader isLoading={isLoading || isLoadingPlaybook}>
            <Heading className="text-base font-semibold text-foreground ">
              {chapterLabel}
            </Heading>
          </SlideoverDialogHeader>

          <SlideoverDialogContent>
            <div className="space-y-8 p-4">
              {(!data && isLoading) || isLoadingPlaybook ? (
                <DefaultSkeleton />
              ) : null}

              {data && chapter
                ? renderContent(
                    chapter,
                    data.assessableParticipantsCount,
                    data.trainingChapterAssessments,
                  )
                : null}
            </div>
          </SlideoverDialogContent>
        </>
      )}
    </SlideoverDialog>
  );
};
